import {
  getCommunications,
  getCommunicationsV2,
  isResponseError
} from "@coxauto-ui/cxm/communications/api";
import { ErrorMessages } from "@coxauto-ui/cxm/communications/util";
import { CenteredSpinner } from "@coxauto-ui/core/components";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";
import styled from "styled-components";
import { ConversationContext, ConversationDetailsContext } from "./context";
import ConversationListApp from "./list/app";
import {
  conversationsListTestId,
  listLoadingIndicatorId,
  messageHistoryError as messageHistoryErrorTestId
} from "./testIds";
import { mergeConversationItems } from "@coxauto-ui/cxm/communications/util";
import { Alert } from "@interstate/components/Alert";
import classNames from "classnames";
import { useCxmFlags } from "@coxauto-ui/core/third-party/launch-darkly";
import { ErrorAlert } from "./components/error-alert";
import type { ErrorDetails } from "./components/error-alert";
import { extractErrorDetails } from "./utils/extract-error-details";

interface ConversationPaneContentProps {
  isFullScreenReplyWindow: boolean;
  showReply: boolean;
}

const StyledContentArea = styled.div`
  overflow-y: scroll;
  flex: 1;
  height: 100%;
  overflow-anchor: none;
  background-color: #f4f4f4;

  &.isFullScreenReplyWindow.showReply {
    display: none;
  }

  /* Basic Alert width constraints to prevent horizontal scroll */
  .MuiAlert-root,
  .alert,
  [role="alert"] {
    max-width: 100%;
    width: auto !important;
    box-sizing: border-box;
  }
`;
const StyledContainerAlert = styled.div`
  padding: 10px;
`;

// polling for new conversation items - can be removed when websockets are introduced
const getCommunicationsCallFrequencyMilliseconds = 15000;

const StyledEasyWayToAddSomeSpace = styled.div`
  height: 48px;
`;

export function ConversationPaneContent({
  isFullScreenReplyWindow,
  showReply
}: ConversationPaneContentProps) {
  const { customerId, customerSystem, activeListTab, env, vinDealerId } =
    useContext(ConversationContext);
  const [communicationsError, setCommunicationsError] = useState<string>("");
  const [communicationsErrorDetails, setCommunicationsErrorDetails] = useState<
    ErrorDetails | undefined
  >(undefined);
  const { "nx.cxm.use-message-details-v2": useMessageDetailsV2 } =
    useCxmFlags();

  const shouldUseV2 = useMessageDetailsV2;

  const pollingTimer = useRef<number>(0);
  const isFirstLoad = useRef<boolean>(true);

  const {
    conversationItems,
    displayConversationItems,
    recentContactEmail,
    recentContactText,
    xSignature,
    setConversationItems,
    setRecentContactEmail,
    setRecentContactText,
    setXSignature
  } = useContext(ConversationDetailsContext);
  const [loading, setLoading] = useState(false);
  const [nextPage, setNextPage] = useState<number | null | undefined>(1);

  const getConversationItems = useCallback(
    async (page?: number, isPolling = true) => {
      if (!isPolling) {
        setLoading(true);
      }

      try {
        const result = shouldUseV2
          ? await getCommunicationsV2(
              {
                customerId,
                consumerRecordSystem: customerSystem,
                startingPoint: page || 1
              },
              env
            )
          : await getCommunications(
              {
                customerId,
                consumerRecordSystem: customerSystem,
                startingPoint: page || 1
              },
              env
            );

        if (isResponseError(result)) {
          if (!isPolling) {
            setCommunicationsError(ErrorMessages.MESSAGE_HISTORY_UNAVAILABLE);
            setCommunicationsErrorDetails({
              ...extractErrorDetails(result),
              dealerId: vinDealerId,
              consumerId: customerId
            });
            setNextPage(null);
          }
        } else if (result.conversation !== null) {
          const mergeResult = mergeConversationItems(
            result.conversation,
            conversationItems
          );
          if (result.xSignature !== xSignature) {
            setXSignature(result.xSignature);
          }
          if (page === undefined || page === 1) {
            if (result.recentContactEmail !== recentContactEmail) {
              setRecentContactEmail(result.recentContactEmail);
            }
            if (result.recentContactText !== recentContactText) {
              setRecentContactText(result.recentContactText);
            }
          }

          if (mergeResult.hasNewItems) {
            setConversationItems(mergeResult.items);
          }
          if (!isPolling) {
            const _nextItem = result?.nextItem;
            setNextPage(_nextItem);
          }
        }
      } catch (_err: unknown) {
        // Catch thrown errors from V2 API
        if (!isPolling) {
          setCommunicationsError(ErrorMessages.MESSAGE_HISTORY_UNAVAILABLE);
          setCommunicationsErrorDetails({
            ...extractErrorDetails(_err),
            dealerId: vinDealerId,
            consumerId: customerId
          });
          setNextPage(null);
        }
      } finally {
        if (!isPolling) {
          setLoading(false);
        }
      }
    },
    [
      customerId,
      customerSystem,
      recentContactEmail,
      recentContactText,
      xSignature,
      setRecentContactEmail,
      setRecentContactText,
      conversationItems,
      setConversationItems,
      env,
      setXSignature,
      shouldUseV2,
      vinDealerId
    ]
  );

  useEffect(() => {
    if (isFirstLoad.current === true) {
      isFirstLoad.current = false;
    } else {
      if (pollingTimer.current) {
        clearInterval(pollingTimer.current);
      }

      setCommunicationsError("");
      setCommunicationsErrorDetails(undefined);
      setLoading(true);

      (async () => {
        try {
          const result = shouldUseV2
            ? await getCommunicationsV2(
                {
                  customerId,
                  consumerRecordSystem: customerSystem,
                  startingPoint: 1
                },
                env
              )
            : await getCommunications(
                {
                  customerId,
                  consumerRecordSystem: customerSystem,
                  startingPoint: 1
                },
                env
              );

          if (isResponseError(result)) {
            setCommunicationsError(ErrorMessages.MESSAGE_HISTORY_UNAVAILABLE);
            setCommunicationsErrorDetails({
              ...extractErrorDetails(result),
              dealerId: vinDealerId,
              consumerId: customerId
            });
          } else {
            setConversationItems(result.conversation);
            setRecentContactEmail(result.recentContactEmail);
            setRecentContactText(result.recentContactText);
            setXSignature(result.xSignature);
          }
        } catch (_err: unknown) {
          // Catch thrown errors from V2 API on initial load
          setCommunicationsError(ErrorMessages.MESSAGE_HISTORY_UNAVAILABLE);
          setCommunicationsErrorDetails({
            ...extractErrorDetails(_err),
            dealerId: vinDealerId,
            consumerId: customerId
          });
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [
    customerId,
    setConversationItems,
    setRecentContactEmail,
    setRecentContactText,
    env,
    setXSignature,
    customerSystem,
    shouldUseV2,
    vinDealerId
  ]);

  useEffect(() => {
    pollingTimer.current = window.setInterval(async () => {
      await getConversationItems();
    }, getCommunicationsCallFrequencyMilliseconds);

    return () => {
      clearInterval(pollingTimer.current);
    };
  }, [getConversationItems]);

  // #region INFINITE SCROLL HANDLING
  // infinite scroll will eventually break on specific channel views
  // until the getCommunications endpoint handles requests by channel
  const [infiniteRef, { rootRef }] = useInfiniteScroll({
    loading,
    hasNextPage: !!nextPage,
    onLoadMore: async () => {
      await getConversationItems(nextPage || 1, false);
    },
    disabled: !!communicationsError,
    rootMargin: "400px 0px 0px 0px"
  });

  const scrollableRootRef = useRef<HTMLDivElement | null>(null);
  const lastScrollDistanceToBottomRef = useRef<number>(0);

  // We keep the scroll position when new items are added etc.
  useEffect(() => {
    const scrollableRoot = scrollableRootRef.current;
    const lastScrollDistanceToBottom =
      lastScrollDistanceToBottomRef.current ?? 0;
    if (scrollableRoot) {
      scrollableRoot.scrollTop =
        scrollableRoot.scrollHeight - lastScrollDistanceToBottom;
    }
  }, [displayConversationItems, rootRef]);

  useEffect(() => {
    const scrollableRoot = scrollableRootRef.current;
    if (scrollableRoot) {
      scrollableRoot.scrollTop = scrollableRoot.scrollHeight;
    }
  }, [activeListTab]);

  const rootRefSetter = useCallback(
    (node: HTMLDivElement) => {
      rootRef(node);
      scrollableRootRef.current = node;
    },
    [rootRef]
  );

  const handleRootScroll = useCallback(() => {
    const rootNode = scrollableRootRef.current;

    if (rootNode) {
      const scrollDistanceToBottom = rootNode.scrollHeight - rootNode.scrollTop;
      lastScrollDistanceToBottomRef.current = scrollDistanceToBottom;
    }
  }, []);
  // #endregion INFINITE SCROLL HANDLING

  return (
    <StyledContentArea
      className={classNames({ isFullScreenReplyWindow, showReply })}
      data-testid={conversationsListTestId}
      ref={rootRefSetter}
      onScroll={handleRootScroll}
    >
      {!loading &&
      !communicationsError &&
      displayConversationItems?.length === 0 ? (
        <StyledContainerAlert>
          <Alert role="info" type="info">
            <span>No messages found</span>
          </Alert>
        </StyledContainerAlert>
      ) : (
        <ConversationListApp />
      )}
      {communicationsError && (
        <ErrorAlert
          details={communicationsErrorDetails}
          message={communicationsError}
          testId={messageHistoryErrorTestId}
        />
      )}
      {!communicationsError && (loading || nextPage) && (
        <span ref={infiniteRef}>
          <CenteredSpinner containerHeight="48px" id={listLoadingIndicatorId} />
        </span>
      )}
      {!showReply && <StyledEasyWayToAddSomeSpace />}
    </StyledContentArea>
  );
}

export default ConversationPaneContent;
